<template>
  <div
    class="pos-product-card"
    :class="{
      'pos-product-card--out-of-stock': isOutOfStock,
      'pos-product-card--added': justAdded,
      'pos-product-card--low-stock': isLowStock,
    }"
    @click="onTap"
    role="button"
    :tabindex="isOutOfStock ? -1 : 0"
    :aria-label="`Add ${product.name} to order`"
    :aria-disabled="isOutOfStock"
    @keyup.enter="onTap"
    @keyup.space.prevent="onTap"
  >
    <div class="pos-product-card__image">
      <img
        v-if="product.public_img_src || product.product_img_src"
        :src="product.public_img_src || product.product_img_src"
        :alt="product.name"
        class="pos-product-card__img"
      />
      <div v-else class="pos-product-card__img-placeholder">
        <i class="fas fa-cannabis"></i>
      </div>
    </div>
    <div class="pos-product-card__info">
      <div class="pos-product-card__name">{{ product.name }}</div>
      <div class="pos-product-card__price">{{ formattedPrice }}</div>
      <stock-badge
        :quantity="product.stock_quantity || 0"
        :low-threshold="product.low_stock_threshold || 5"
      />
    </div>
    <div
      class="pos-product-card__detail-btn"
      @click.stop="$emit('view-details', product)"
      role="button"
      aria-label="View product details"
    >
      <i class="mdi mdi-information-outline"></i>
    </div>
  </div>
</template>

<script>
import StockBadge from "@/components/shared/stockBadge";

export default {
  name: "posProductCard",
  components: { StockBadge },
  props: {
    product: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      justAdded: false,
    };
  },
  computed: {
    isOutOfStock() {
      return (this.product.stock_quantity || 0) === 0;
    },
    isLowStock() {
      const qty = this.product.stock_quantity || 0;
      const threshold = this.product.low_stock_threshold || 5;
      return qty > 0 && qty <= threshold;
    },
    formattedPrice() {
      const price = parseFloat(this.product.price) || 0;
      return `R${price.toFixed(2)}`;
    },
  },
  methods: {
    onTap() {
      if (this.isOutOfStock || this.disabled) return;
      this.$emit("add-to-order", this.product);
      this.justAdded = true;
      setTimeout(() => {
        this.justAdded = false;
      }, 200);
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/design/tokens/brand";
@import "~@/design/tokens/surfaces";
@import "~@/design/tokens/typography";
@import "~@/design/tokens/spacing";
@import "~@/design/tokens/feedback";

.pos-product-card {
  display: flex;
  align-items: center;
  background: $surface-card;
  border: 1px solid $border-default;
  border-radius: 8px;
  padding: $space-3;
  min-height: 120px;
  cursor: pointer;
  transition: border-color 0.15s, transform 0.15s, opacity 0.15s;
  user-select: none;

  &:hover:not(.pos-product-card--out-of-stock) {
    border-color: $brand-primary;
    transform: translateY(-2px);
  }

  &:active:not(.pos-product-card--out-of-stock) {
    border-color: $brand-primary-dark;
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid $brand-primary;
    outline-offset: 2px;
  }

  &--out-of-stock {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &--added {
    border-color: $feedback-success;
    box-shadow: 0 0 0 2px rgba($feedback-success, 0.3);
  }

  &__image {
    flex-shrink: 0;
    width: 60px;
    height: 60px;
    margin-right: $space-3;
  }

  &__img {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 8px;
  }

  &__img-placeholder {
    width: 60px;
    height: 60px;
    border-radius: 8px;
    background: $surface-elevated;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $text-muted;
    font-size: 24px;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: $type-body-lg;
    font-weight: 600;
    color: $text-primary;
    margin-bottom: $space-1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__price {
    font-size: $type-body-lg;
    font-weight: 700;
    color: $brand-secondary;
    margin-bottom: $space-1;
  }

  &__detail-btn {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: $text-muted;
    font-size: 20px;
    transition: color 0.15s, background 0.15s;
    cursor: pointer;

    &:hover {
      color: $brand-primary;
      background: rgba($brand-primary, 0.1);
    }
  }
}
</style>
